// Lightweight i18n table for hardcoded UI strings.
// Default language is 'de' — the widget's primary clients are German
// municipalities. English strings are kept for the original Remstal Tourismus
// integration and for any future English embeds.

export type Language = "de" | "en";

export interface UiStrings {
  fallbackName: string;
  statusOnline: string;
  statusListening: string;
  statusConnecting: string;
  inputPlaceholder: string;
  // Greeting renders as: `${greetingHello} ${name}, ${greetingTail}\n${greetingFollowup}`
  greetingHello: string;
  greetingTail: string;
  greetingFollowup: string;
  voiceStartRecording: string;
  voiceStopRecording: string;
  stopConversation: string;
  compactView: string;
  openChat: string;
  openAssistant: string;
  textMode: string;
  voiceMode: string;
  startVoice: string;
  userLabel: string;
  buttonVoice: string;
  buttonSwitchToVoice: string;
  buttonVoiceActive: string;
  buttonStop: string;
  buttonClose: string;
  buttonSend: string;
  agentThinking: string;
  idleEndedMessage: string;
  sessionEndedMessage: string;
}

const DE: UiStrings = {
  fallbackName: "Digitaler Assistent",
  statusOnline: "Online",
  statusListening: "Höre zu …",
  statusConnecting: "Verbinde …",
  inputPlaceholder: "Nachricht eingeben …",
  // Two-line greeting. With a widgetName: "👋 Hallo, ich bin {Name}." +
  // followup. Without a widgetName: greetingHello + greetingTail (no name).
  greetingHello: "👋 Hallo, ich bin",
  greetingTail: "Ihr digitaler Assistent.",
  greetingFollowup: "Wie kann ich Ihnen helfen?",
  voiceStartRecording: "Aufnahme starten",
  voiceStopRecording: "Aufnahme stoppen",
  stopConversation: "Gespräch beenden",
  compactView: "Verkleinern",
  openChat: "Chat öffnen",
  openAssistant: "Assistent öffnen",
  textMode: "Text",
  voiceMode: "Sprache",
  startVoice: "Sprachmodus starten",
  userLabel: "Sie",
  buttonVoice: "Sprache",
  buttonSwitchToVoice: "Zu Sprache",
  buttonVoiceActive: "Sprache aktiv",
  buttonStop: "Beenden",
  buttonClose: "Schließen",
  buttonSend: "Senden",
  agentThinking: "Denkt nach …",
  idleEndedMessage:
    "Die Sitzung wurde nach einer kurzen Inaktivität beendet. Sie können den Assistenten jederzeit erneut starten.",
  sessionEndedMessage:
    "Die Sitzung wurde beendet. Sie können den Assistenten jederzeit erneut starten.",
};

const EN: UiStrings = {
  fallbackName: "Assistant",
  statusOnline: "Online",
  statusListening: "Listening...",
  statusConnecting: "Connecting...",
  inputPlaceholder: "Type your message...",
  greetingHello: "👋 Hello! I'm",
  greetingTail: "your digital assistant.",
  greetingFollowup: "How can I help you today?",
  voiceStartRecording: "Start recording",
  voiceStopRecording: "Stop recording",
  stopConversation: "Stop conversation",
  compactView: "Compact view",
  openChat: "Open chat",
  openAssistant: "Open assistant",
  textMode: "Text",
  voiceMode: "Voice",
  startVoice: "Start voice mode",
  userLabel: "You",
  buttonVoice: "Voice",
  buttonSwitchToVoice: "Switch to voice",
  buttonVoiceActive: "Voice active",
  buttonStop: "End",
  buttonClose: "Close",
  buttonSend: "Send",
  agentThinking: "Thinking …",
  idleEndedMessage:
    "The session ended after a short period of inactivity. You can restart the assistant at any time.",
  sessionEndedMessage:
    "The session ended. You can restart the assistant at any time.",
};

export function getStrings(lang?: Language | string | null): UiStrings {
  if (lang === "en") return EN;
  return DE;
}

export function isLanguage(value: unknown): value is Language {
  return value === "de" || value === "en";
}
